/**
 * Utility functions for managing error logs stored by ErrorBoundary
 */

interface ErrorLogEntry {
  timestamp: string;
  sectionName: string;
  errorMessage: string;
  errorStack: string | undefined;
}

const ERROR_LOG_KEY = 'app_errors';

/**
 * Retrieves all error logs from localStorage
 * @returns Array of error log entries
 */
export const getErrorLogs = (): ErrorLogEntry[] => {
  try {
    const logsJson = localStorage.getItem(ERROR_LOG_KEY);
    if (!logsJson) return [];
    return JSON.parse(logsJson);
  } catch (error) {
    console.error('Failed to retrieve error logs:', error);
    return [];
  }
};

/**
 * Clears all error logs from localStorage
 */
export const clearErrorLogs = (): void => {
  try {
    localStorage.removeItem(ERROR_LOG_KEY);
    console.log('[ErrorLogs] All error logs cleared');
  } catch (error) {
    console.error('Failed to clear error logs:', error);
  }
};

/**
 * Gets the count of error logs
 * @returns Number of error logs
 */
export const getErrorLogCount = (): number => {
  const logs = getErrorLogs();
  return logs.length;
};

/**
 * Gets error logs for a specific section
 * @param sectionName - Name of the section to filter by
 * @returns Array of error log entries for that section
 */
export const getErrorLogsBySection = (sectionName: string): ErrorLogEntry[] => {
  const logs = getErrorLogs();
  return logs.filter(log => log.sectionName === sectionName);
};

/**
 * Gets error logs within a date range
 * @param startDate - Start date (inclusive)
 * @param endDate - End date (inclusive)
 * @returns Array of error log entries within the date range
 */
export const getErrorLogsByDateRange = (startDate: Date, endDate: Date): ErrorLogEntry[] => {
  const logs = getErrorLogs();
  return logs.filter(log => {
    const logDate = new Date(log.timestamp);
    return logDate >= startDate && logDate <= endDate;
  });
};

/**
 * Prints error logs to console in a formatted table
 */
export const printErrorLogs = (): void => {
  const logs = getErrorLogs();
  if (logs.length === 0) {
    console.log('[ErrorLogs] No error logs found');
    return;
  }

  console.log(`[ErrorLogs] Found ${logs.length} error(s):`);
  console.table(
    logs.map(log => ({
      Time: new Date(log.timestamp).toLocaleString(),
      Section: log.sectionName,
      Error: log.errorMessage
    }))
  );
};

/**
 * Exports error logs as JSON string
 * @returns JSON string of all error logs
 */
export const exportErrorLogs = (): string => {
  const logs = getErrorLogs();
  return JSON.stringify(logs, null, 2);
};

/**
 * Downloads error logs as a JSON file
 */
export const downloadErrorLogs = (): void => {
  const logs = getErrorLogs();
  const dataStr = JSON.stringify(logs, null, 2);
  const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

  const exportFileDefaultName = `error-logs-${new Date().toISOString().split('T')[0]}.json`;

  const linkElement = document.createElement('a');
  linkElement.setAttribute('href', dataUri);
  linkElement.setAttribute('download', exportFileDefaultName);
  linkElement.click();

  console.log(`[ErrorLogs] Downloaded ${logs.length} error log(s) as ${exportFileDefaultName}`);
};

// Make functions available in browser console for debugging
if (typeof window !== 'undefined') {
  (window as any).errorLogs = {
    get: getErrorLogs,
    clear: clearErrorLogs,
    count: getErrorLogCount,
    bySection: getErrorLogsBySection,
    byDateRange: getErrorLogsByDateRange,
    print: printErrorLogs,
    export: exportErrorLogs,
    download: downloadErrorLogs
  };
}
